/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import {
  MessageData,
  ServicePageNotification,
} from "../../models/service-page-notification.model";
import { ContextualRequestedBannerAlert } from "sirius-platform-support-library/shared/banner-alerts/banner-alert";
import {
  ActionType,
  ActionTypeModel,
  LinkActionType,
} from "../../models/action-type.model";
import { BannerAlertAction } from "sirius-platform-support-library/shared/banner-alerts/banner-alert-action";
import {
  ActionTargetEnum,
  ActionTypeEnum,
} from "sirius-platform-support-library/models/common";
import { v4 as uuidv4 } from "uuid";
import { LocalizedDateUtility } from "../services/localized-date.utility";
import { TranslationService } from "sirius-platform-support-library/shared/localization/translations/translation.service";
import { ObjectUtility } from "../../shared/object-utility";
import {
  ACTION_KEY,
  ActionMapper,
  BANNER_KEY,
  BANNER_START_FROM,
  BANNER_TEXT,
  BANNERS_ACTIONS_LINK,
  BANNERS_ACTIONS_TEXT,
  BANNERS_TO,
  LINK_KEY,
  PatternTypes,
  TEXT_KEY,
  TypeAndIconBannerMap,
} from "./banners.constants";

export class BannerAlertsMapper {
  DATE_PATTERN: PatternTypes = "medium";
  TIME_PATTERN: PatternTypes = "short";

  public namespace: string;
  public localizedDate: LocalizedDateUtility;

  private readonly translationService: TranslationService;

  public constructor(
    namespace: string,
    localizedDate: LocalizedDateUtility,
    translationService: TranslationService
  ) {
    this.namespace = namespace;
    this.localizedDate = localizedDate;
    this.translationService = translationService;
  }

  public mapToContextualRequestedBannersAlert(
    context: any,
    bannerAlerts: ServicePageNotification[],
    currentLang: string,
    supportedLocale?: string[]
  ): ContextualRequestedBannerAlert[] {
    return (bannerAlerts ?? []).map((bannerAlert: ServicePageNotification) =>
      this.mapToContextualRequestedBannerAlert(
        context,
        bannerAlert,
        currentLang,
        supportedLocale
      )
    );
  }

  public mapToContextualRequestedBannerAlert(
    context: any,
    bannerAlert: ServicePageNotification,
    currentLang: string,
    supportedLocale?: string[]
  ): ContextualRequestedBannerAlert {
    const { type, iconType } =
      TypeAndIconBannerMap[bannerAlert.severity.toUpperCase()];

    const localizationResources: Record<string, any> =
      this.mapBannerLocalizationResources(bannerAlert.localeToMessage);
    const actionMapped = this.mapActionLocalizationResources(
      bannerAlert?.action?.linkAction,
      currentLang,
      supportedLocale
    );
    const mergedResources = ObjectUtility.mergeObjects(
      localizationResources,
      actionMapped,
      BANNER_KEY
    );
    const localizationBannerCodes = this.translateLocale(
      BANNER_TEXT,
      bannerAlert.messageData,
      BANNER_START_FROM,
      BANNERS_TO
    );

    const banner = {
      id: bannerAlert.id,
      namespace: this.namespace,
      context: context,
      type: type,
      iconResource: iconType,
      localizationCode: localizationBannerCodes,
      dismissButtonVisible: bannerAlert.dismissible,
      actions: bannerAlert?.action
        ? ActionMapper[bannerAlert.action.type?.toUpperCase()] ===
          ActionTypeEnum.URL
          ? [this.mapAction(bannerAlert.action, bannerAlert)]
          : []
        : [],
      localizedResources: mergedResources,
      onTranslationsRequested: this.onTranslationsRequested.bind(this),
    };

    return banner;
  }

  private mapBannerLocalizationResources(localeToMessage: {
    [key: string]: string;
  }): Record<string, any> {
    const mappedLocales: Record<string, any> = {};

    Object.keys(localeToMessage).forEach((key) => {
      const bannerLocale = {
        [BANNER_KEY]: {
          [TEXT_KEY]: localeToMessage[key],
        },
      };
      mappedLocales[key] = bannerLocale;
    });
    return mappedLocales;
  }

  private mapAction(
    action: ActionTypeModel,
    pageNotification: ServicePageNotification
  ): BannerAlertAction {
    if (
      !pageNotification?.action ||
      pageNotification?.action?.type.toUpperCase() === ActionType.NULL ||
      pageNotification?.action?.type.toUpperCase() === ActionType.NONE
    ) {
      return;
    }
    const isOpenInNewTab = pageNotification?.action?.linkAction?.openInNewTab;
    return {
      type: ActionMapper[action.type.toUpperCase()],
      code: uuidv4().toLowerCase(),
      action: BANNERS_ACTIONS_LINK,
      localizationCode: BANNERS_ACTIONS_TEXT,
      target: isOpenInNewTab
        ? ActionTargetEnum.NEW_TAB
        : ActionTargetEnum.SAME_TAB,
      isActionTranslatable: true,
    };
  }

  private mapActionLocalizationResources(
    linkAction: LinkActionType,
    currentLang: string,
    supportedLocale?: string[]
  ): Record<string, any> {
    if (!linkAction) {
      return {};
    }
    const mappedLocales: Record<string, any> = {};

    if (linkAction.useLocalized) {
      Object.keys(linkAction.localizedLinks).forEach((key) => {
        const bannerLocale = {
          [BANNER_KEY]: {
            [ACTION_KEY]: {
              [TEXT_KEY]: linkAction.localizedLinks[key].text,
              [LINK_KEY]: linkAction.localizedLinks[key].url,
            },
          },
        };
        mappedLocales[key] = bannerLocale;
      });
    } else {
      if (!supportedLocale) {
        const bannerLocale = {
          [BANNER_KEY]: {
            [ACTION_KEY]: {
              [TEXT_KEY]: linkAction.universalLink.text,
              [LINK_KEY]: linkAction.universalLink.url,
            },
          },
        };
        mappedLocales[currentLang] = bannerLocale;
      } else {
        Object.values(supportedLocale).forEach((key) => {
          const bannerLocale = {
            [BANNER_KEY]: {
              [ACTION_KEY]: {
                [TEXT_KEY]: linkAction.universalLink.text,
                [LINK_KEY]: linkAction.universalLink.url,
              },
            },
          };
          mappedLocales[key] = bannerLocale;
        });
      }
    }
    return mappedLocales;
  }

  private translateLocale(
    textLocale: string,
    messageData?: MessageData,
    startsFromTranslated?: string,
    toTranslated?: string
  ): string {
    let constructedMessage = "";

    if (messageData?.service) {
      constructedMessage = messageData.service + " - ";
    }
    constructedMessage = constructedMessage + " " + textLocale;

    if (messageData?.eventStartDate) {
      constructedMessage =
        constructedMessage +
        " " +
        startsFromTranslated +
        " " +
        `${this.getFormattedDate(
          messageData.eventStartDate,
          this.DATE_PATTERN
        )}, ${this.getFormattedDate(
          messageData.eventStartDate,
          this.TIME_PATTERN
        )}`;
    }
    if (messageData?.eventEndDate) {
      constructedMessage =
        constructedMessage +
        " " +
        toTranslated +
        " " +
        `${this.getFormattedDate(
          messageData.eventEndDate,
          this.DATE_PATTERN
        )}, ${this.getFormattedDate(
          messageData.eventEndDate,
          this.TIME_PATTERN
        )}`;
    }
    return constructedMessage;
  }

  private getFormattedDate(eventDate: any, format: PatternTypes): any {
    return this.localizedDate.transform(eventDate, format);
  }

  private async onTranslationsRequested(): Promise<Record<string, any>> {
    return await this.translationService.getTranslationsBySupportedLocales();
  }
}
